// Utility functions for UTM parameter handling

const STORAGE_KEY = 'workingdom_utm_params';
const TRACKED_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_campaign_id',
  'campaign_id',
  'campaignid',
  'cmapign_id',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid'
];

/**
 * Extracts UTM parameters from a URL or URLSearchParams
 * @param {string|URLSearchParams} urlOrParams - URL string or URLSearchParams object
 * @returns {Object} Object containing UTM parameters
 */
export const extractUTMParams = (urlOrParams) => {
  if (!urlOrParams) return {};
  let searchParams;
  
  if (typeof urlOrParams === 'string') {
    try {
      const url = new URL(urlOrParams, window.location.origin);
      searchParams = url.searchParams;
    } catch {
      searchParams = new URLSearchParams(urlOrParams.startsWith('?') ? urlOrParams : `?${urlOrParams}`);
    }
  } else {
    searchParams = urlOrParams;
  }
  
  const utmParams = {};
  TRACKED_KEYS.forEach(key => {
    const value = searchParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });
  
  return utmParams;
};

/**
 * Gets UTM parameters from localStorage
 * @returns {Object} UTM parameters from localStorage
 */
export const getStoredUTMParams = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error parsing stored UTM params:', error);
    return {};
  }
};

/**
 * Stores UTM parameters in localStorage (merging with existing)
 * @param {Object} utmParams - UTM parameters to store
 */
export const storeUTMParams = (utmParams) => {
  if (utmParams && Object.keys(utmParams).length > 0) {
    try {
      const existing = getStoredUTMParams();
      const merged = { ...existing, ...utmParams };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch (e) {
      console.error(e);
    }
  }
};

/**
 * Clears UTM parameters from localStorage
 */
export const clearStoredUTMParams = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error(e);
  }
};

/**
 * Appends UTM parameters to a URL
 * @param {string} baseUrl - The base URL to append UTM parameters to
 * @param {Object} utmParams - UTM parameters object (optional, will use stored if not provided)
 * @returns {string} URL with UTM parameters appended
 */
export const appendUTMParamsToUrl = (baseUrl, utmParams = null) => {
  const params = (utmParams && Object.keys(utmParams).length > 0) ? utmParams : getStoredUTMParams();
  if (!params || Object.keys(params).length === 0) {
    return baseUrl;
  }
  
  try {
    const url = new URL(baseUrl, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value);
      }
    });
    return url.pathname + url.search;
  } catch {
    return baseUrl;
  }
};

export const appendUTMParamsToUrlEnhanced = appendUTMParamsToUrl;